// Actions · useNotify
//
// Thin wrapper around `App.useApp().message` (the context-aware AntD toast API —
// never the static `message` import, which can't read theme/config context).
// Wraps the toast content in `<span data-testid="toast-{type}">` so e2e specs
// assert the outcome by testid + text instead of a locale-coupled getByText.
//
// Usage:
//   const notify = useNotify();
//   notify.success("Zapisano pomyślnie");
//   notify.error("Brak ID rozszerzenia BDO");
//
// Spec:  await expect(page.getByTestId("toast-error")).toContainText("Brak ID …");

import React, { useMemo } from "react";
import { App } from "antd";

type ToastType = "success" | "error" | "warning" | "info";

export interface Notify {
  success: (content: React.ReactNode) => void;
  error: (content: React.ReactNode) => void;
  warning: (content: React.ReactNode) => void;
  info: (content: React.ReactNode) => void;
}

export function useNotify(): Notify {
  const { message } = App.useApp();

  // Stable identity across renders (AntD's `message` instance is itself stable)
  // so consumers can list `notify` in useCallback/useMemo deps without the React
  // Compiler bailing out ("existing memoization could not be preserved").
  return useMemo(() => {
    const show = (type: ToastType) => (content: React.ReactNode) => {
      message[type](<span data-testid={`toast-${type}`}>{content}</span>);
    };
    return {
      success: show("success"),
      error:   show("error"),
      warning: show("warning"),
      info:    show("info"),
    };
  }, [message]);
}
